import {
  IFieldDefinition,
  LayoutElement,
  isFieldElement,
  isInputFieldElement,
  isLayoutTableFieldElement,
  isOptionsFieldElement,
} from "@entaconsulting/form";
import { Checkbox, FormControlLabel, Stack } from "@mui/material";
import EntaAccordion from "common/components/EntaAccordion";
import { PanelSections } from "developer/edit/constants";
import useColumnEdited from "developer/edit/hooks/useColumnEdited";
import useDynamicField from "developer/edit/hooks/useDynamicField";
import useIsExpanded from "developer/edit/hooks/useIsExpanded";
import DynamicField from "./DynamicField";
import ExpressionField from "./commonInputFields/ExpressionField";
import ScrollOnOverflowField from "./commonInputFields/ScrollOnOverflow";

const BehaviorSection = () => {
  const { isExpanded, handleChangeIsExpanded, isDefaultExpanded, hide } =
    useIsExpanded(PanelSections.Behavior);
  const {
    input: { value: element },
  } = useDynamicField<LayoutElement>("element");
  const {
    input: { value: fieldDef },
  } = useDynamicField<IFieldDefinition>("fieldDef");

  const columnEdited = useColumnEdited();
  const isFieldEl = isFieldElement(element);
  const isInputFieldEl = isFieldEl && isInputFieldElement(element, fieldDef);
  const isTableFieldEl = isLayoutTableFieldElement(element);
  const isOptionsFieldEl =
    isFieldEl && isOptionsFieldElement(element, fieldDef);
  const isDateFieldEl =
    isFieldEl && ["date", "datetime"].includes(fieldDef.type);
  const isBooleanFieldEl = isFieldEl && fieldDef.type === "boolean";
  const isTristateFieldEl = isFieldEl && fieldDef.type === "tristate";
  const isAttachmentFieldEl = isFieldEl && fieldDef.type === "attachment";

  const getTargetProp = (prop: string) => {
    return columnEdited
      ? `listFields.fields.${columnEdited.index}.${prop}`
      : prop;
  };

  if (hide) return null;

  return (
    <EntaAccordion
      title="Behavior"
      expanded={isExpanded}
      defaultExpanded={isDefaultExpanded}
      onChange={handleChangeIsExpanded}
    >
      <Stack spacing={1}>
        {(isInputFieldEl ||
          isTableFieldEl ||
          isOptionsFieldEl ||
          isDateFieldEl ||
          isBooleanFieldEl ||
          isAttachmentFieldEl ||
          isTristateFieldEl) && (
          <DynamicField name="element.readonly">
            {(props) => (
              <ExpressionField
                type="select"
                label="Readonly"
                targetProp={getTargetProp("readonly")}
                name={props.input.name}
                value={props.input.value}
                onChange={props.input.onChange}
              />
            )}
          </DynamicField>
        )}
        <DynamicField name="element.visibility">
          {(props) => (
            <ExpressionField
              type="select"
              targetProp={getTargetProp("visibility")}
              label="Visibility"
              name={props.input.name}
              value={props.input.value}
              onChange={props.input.onChange}
            />
          )}
        </DynamicField>
        {(isInputFieldEl ||
          isTableFieldEl ||
          isOptionsFieldEl ||
          isDateFieldEl ||
          isAttachmentFieldEl ||
          isBooleanFieldEl ||
          isTristateFieldEl) && (
          <DynamicField name="element.computedExpression">
            {(props) => (
              <ExpressionField
                type="text"
                targetProp={getTargetProp("computedExpression")}
                label="Computed Expression"
                name={props.input.name}
                value={props.input.value}
                onChange={props.input.onChange}
              />
            )}
          </DynamicField>
        )}
        {columnEdited &&
          (isInputFieldEl ||
            isTableFieldEl ||
            isOptionsFieldEl ||
            isDateFieldEl ||
            isAttachmentFieldEl ||
            isBooleanFieldEl ||
            isTristateFieldEl) && (
            <DynamicField name="element.columnVisibility">
              {(props) => (
                <ExpressionField
                  type="select"
                  targetProp={getTargetProp("columnVisibility")}
                  label="Column Visibility"
                  name={props.input.name}
                  value={props.input.value}
                  onChange={props.input.onChange}
                />
              )}
            </DynamicField>
          )}
        {columnEdited && (
          <Stack spacing={1} width={1 / 2}>
            <DynamicField name="element.scrollOnOverflow">
              {(props) => (
                <ScrollOnOverflowField
                  name={props.input.name}
                  value={props.input.value}
                  onChange={props.input.onChange}
                />
              )}
            </DynamicField>
          </Stack>
        )}
        {isTableFieldEl && (
          <>
            <Stack direction="row" spacing={1}>
              <DynamicField name="element.showErrorMarkers" type="checkbox">
                {(props) => (
                  <FormControlLabel
                    name={props.input.name}
                    label="Show Error Markers"
                    control={
                      <Checkbox
                        checked={props.input.checked}
                        onChange={props.input.onChange}
                        name={props.input.name}
                        color="primary"
                        size="small"
                      />
                    }
                  />
                )}
              </DynamicField>
            </Stack>
            <Stack direction="row" spacing={1}>
              <DynamicField name="element.canAdd" type="checkbox">
                {(props) => (
                  <FormControlLabel
                    name={props.input.name}
                    label="Can Add"
                    control={
                      <Checkbox
                        checked={props.input.checked}
                        onChange={props.input.onChange}
                        name={props.input.name}
                        color="primary"
                        size="small"
                      />
                    }
                  />
                )}
              </DynamicField>
              <DynamicField name="element.canDelete" type="checkbox">
                {(props) => (
                  <FormControlLabel
                    name={props.input.name}
                    label="Can Delete"
                    control={
                      <Checkbox
                        checked={props.input.checked}
                        onChange={props.input.onChange}
                        name={props.input.name}
                        color="primary"
                        size="small"
                      />
                    }
                  />
                )}
              </DynamicField>
            </Stack>
            <Stack direction="row" spacing={1}>
              <DynamicField name="element.canSort" type="checkbox">
                {(props) => (
                  <FormControlLabel
                    name={props.input.name}
                    label="Can Sort"
                    control={
                      <Checkbox
                        checked={props.input.checked}
                        onChange={props.input.onChange}
                        name={props.input.name}
                        color="primary"
                        size="small"
                      />
                    }
                  />
                )}
              </DynamicField>
              <DynamicField name="element.canShrink" type="checkbox">
                {(props) => (
                  <FormControlLabel
                    name={props.input.name}
                    label="Can Shrink"
                    control={
                      <Checkbox
                        checked={props.input.checked}
                        onChange={props.input.onChange}
                        name={props.input.name}
                        color="primary"
                        size="small"
                      />
                    }
                  />
                )}
              </DynamicField>
            </Stack>
          </>
        )}
      </Stack>
    </EntaAccordion>
  );
};

export default BehaviorSection;
